import { IncidentsGridView } from '../view/IncidentsGridView';
import { templateIncidentsGrid } from '../view/template';
import { IncidentsGridRepository } from '../repository/IncidentsGridRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { IncidentsGridWebComponent } from '..';
import {
  AdditionalElementInfo, BulkActionFields,
  DialogActionTexts,
  DropDownSelectEvents,
  ExtraParametersSchema,
  INCIDENT_TYPE,
  IncidentEvents,
  PRIORITY
} from '../types';
import {
  Categories,
  convertGenericViewConfigToGridViewConfig,
  SmartViewEvents,
  SmartViewsCreateRequest,
  SmartViewsUpdateRequest,
  SmartViewType,
} from '../../../grid/genericGridSmartViews/types';
import { SideNavEvents } from '../../../ui/gzPushBodyLayout/types';
import { SideMenuEvents } from '../../../ui/gzSideMenu/types';
import { GridEvents } from '../../../grid/types/common';
import { EventsName } from '../../../xdr/organizationalImpact/types/types';
import { PanelType } from '../../incidentsSideMenu/types';
import { EdrIncidentDetailsPanelEvents } from '../../edrIncidentDetails/types';
import { SelectEvents } from '../../../ui/gzSelect/types';
import { translateFn } from '../../../utils/translator.helper';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GzDialog } from '../../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { GzTextareaWebComponent } from '../../../ui/gzInput';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { GzToast, ToastPositionX, ToastPositionY, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { GzCheckboxWebComponent } from '../../../ui/gzCheckbox';
import { Feature } from '@nirvana/shared/constants';
import {
  ElementClass,
  elementClassListAdd,
  elementClassListRemove,
  toggleActive,
} from '../../../utils/htmlElement.utils';
import { GenericGridSmartViewsHelpers } from '../../../grid/genericGridSmartViews/helpers/GenericGridSmartViewsHelpers';
import { IncidentsGridDataProvider } from '../dataProvider/IncidentsGridDataProvider';
import { GzButtonState } from '../../../ui/gzButton/types';
import {GzSingleSelectWebComponent} from "../../../ui/gzSingleSelectDropdown";
import {GzSelectMenuWithButtonEvents} from "../../../ui/gzSelectMenuWithButton/types";

export class IncidentsGridController extends BaseWebComponentController {
  public component: IncidentsGridWebComponent;
  public dataRepository: IncidentsGridRepository;
  public view: IncidentsGridView;
  public viewConstructor: typeof IncidentsGridView;
  public dataRepositoryConstructor: typeof IncidentsGridRepository;

  private selectedItems: Array<any> = [];

  constructor(component: IncidentsGridWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: IncidentsGridView,
      viewTemplate: templateIncidentsGrid,
      dataRepositoryConstructor: IncidentsGridRepository,
    });
  }

  async getDataForView(): Promise<ExtraParametersSchema> {
    const incidentsData = await this.dataRepository.getDataForView();

    return {
      incidentsData,
      externalFilters: this.component.externalFilters,
    };
  }

  public computeEventListeners() {
    return [
      {
        element: this.view.dom.$grid,
        event: SmartViewEvents.SMART_VIEW_SAVED,
        callback: this.updateViewProps,
      },
      {
        element: this.view.dom.$grid,
        event: SmartViewEvents.SHOW_SAVE_VIEW_AS_DIALOG,
        callback: this.showSaveAsDialog,
      },
      {
        element: this.view.dom.$grid,
        event: SmartViewEvents.SMART_VIEW_FAVORITE_CHANGED,
        callback: this.handleSmartViewFavoriteChanged,
      },
      {
        element: this.view.dom.$sideMenu,
        event: EventsName.RESOURCES_CLICK,
        callback: this.handleResourceIconClicked,
      },
      {
        element: this.view.dom.$sideMenu,
        event: EventsName.ENTITIES_CLICK,
        callback: this.handleEntityIconClicked,
      },
      {
        element: this.view.dom.$gridSmartViews,
        event: SmartViewEvents.SMART_VIEW_SELECTED,
        callback: this.handleSmartViewClicked,
      },
      {
        element: this.view.dom.$pushBodyLayout,
        event: SideNavEvents.OPEN,
        callback: this.handleSidenavOpened,
      },
      {
        element: this.view.dom.$pushBodyLayout,
        event: SideNavEvents.CLOSE,
        callback: this.handleSidenavClosed,
      },
      {
        element: this.view.dom.$gridSmartViews,
        event: SmartViewEvents.SMART_VIEW_NAME_UPDATED,
        callback: this.handleSmartViewNameUpdated,
      },
      {
        element: this.view.dom.$gridSmartViews,
        event: SmartViewEvents.SMART_VIEW_AFTER_CREATE,
        callback: this.handleSmartViewAfterCreate,
      },
      {
        element: this.view.dom.$sideMenu,
        event: SideMenuEvents.CLOSE,
        callback: this.handleRightSideNavClosed,
      },
      {
        element: this.view.dom.$pushBodyLayout,
        event: SideNavEvents.NAV_STOP_RESIZE,
        callback: this.handleNavResizeEvent,
      },
      {
        element: this.view.dom.$gridSmartViews,
        event: SmartViewEvents.SMART_VIEW_AFTER_DELETE,
        callback: this.handleSmartViewAfterDelete,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROW_CLICKED,
        callback: this.handleRowClicked,
      },
      {
        element: this.view.dom.$sideMenu,
        event: EdrIncidentDetailsPanelEvents.SHOW_EDR_GRAPH,
        callback: this.handleEdrShowGraphClicked,
      },
      {
        element: this.view.dom.$sideMenu,
        event: EdrIncidentDetailsPanelEvents.SHOW_EDR_EVENTS,
        callback: this.handleEdrShowEventsClicked,
      },
      {
        element: this.view.dom.$sideMenu,
        event: IncidentEvents.SHOW_INCIDENT,
        callback: this.handleShowIncident,
      },
      {
        element: this.view.dom.$sideMenu,
        event: EdrIncidentDetailsPanelEvents.ALERT_OPENED,
        callback: this.handleAlertOpened,
      },
      {
        element: this.view.dom.$sideMenu,
        event: EdrIncidentDetailsPanelEvents.ALERT_CLOSED,
        callback: this.handleAlertClosed,
      },
      {
        element: this.view.dom.$grid,
        event: DropDownSelectEvents.viewEvents,
        callback: this.handleViewIncidentEvents,
      },
      {
        element: this.view.dom.$grid,
        event: DropDownSelectEvents.prioritize,
        callback: this.handlePrioritizeInlineAction,
      },
      {
        element: this.view.dom.$grid,
        event: DropDownSelectEvents.assign,
        callback: this.handleAssignInlineAction,
      },
      {
        element: this.view.dom.changeStatusBulk.$button,
        event: SelectEvents.SELECTION_CHANGED,
        callback: this.handleChangeStatusBulkSelectionChanged,
      },
      {
        element: this.view.dom.prioritizeBulk.$button,
        event: SelectEvents.SELECTION_CHANGED,
        callback: this.handlePrioritizeBulkSelectionChanged,
      },
      {
        element: this.view.dom.$assignBulkButton,
        event: GzSelectMenuWithButtonEvents.CHANGE,
        callback: this.handleAssignBulkChange,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: this.handleSelectionChange,
      },
      {
        element: this.view.dom.$demoModeButton,
        event: 'click',
        callback: this.enableDemoModeClick,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.EXPORT_CSV,
        callback: this.exportCsv,
      }
    ];
  }

  private processFiltersForCsvDownload(filters) {
    for (const filter of filters) {
      if (Array.isArray(filter.value)) {
        filter.value = filter.value.map(i => (i === null ? 'null' : i));
      } else {
        filter.value = filter.value ?? 'null';
      }
    }
  }

  private async exportCsv(event: WebComponentEvent) {
    const incidentsGridDataProvider = this.view.dom.$grid.dataProvider as IncidentsGridDataProvider;
    const filters = incidentsGridDataProvider.getConvertedFilters(event.detail.filters);
    this.processFiltersForCsvDownload(filters);
    const smartViewId = event.detail.activeViewId;
    const fileName = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'UnifiedIncidents',
      method: 'generateCsvFilename',
      payload: {
        smartViewId: smartViewId,
      },
    });
    try {
      this.view.dom.$grid.controller?.updateCsvButtonState(
        GzButtonState.IS_LOADING,
        translateFn('Change the current filter selection to generate a new export.')
      );
      //Start downloading the csv
      const downloadLink = app.buildDownloadUrl('UnifiedIncidents', 'exportIncidentsRecordsAsCsv', null, 'CSV');
      Application.lib.DownloadManager.download(
        downloadLink,
        { filters: filters, fileName: fileName, displayedColumns: event.detail.activeDisplayedColumns },
        true
      );
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: translateFn(`Started exporting {fileName}.`, {
          fileName,
        }),
        severity: ToastSeverity.SUCCESS,
      });
    } catch (error) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: this.dependencies.translateFn(`Failed exporting {fileName}.`, {
          fileName,
        }),
        severity: ToastSeverity.ERROR,
      });
      this.view.dom.$grid.controller?.updateCsvButtonState(
        GzButtonState.ENABLED,
        this.dependencies.translateFn('Export view')
      );
    }
  }

  async enableDemoModeClick() {
    const xdrDemoModeStatus = Application.Main.getUser()['xdrDemoModeEnabled'];
    try {
      await this.dependencies.api.clients.UnifiedIncidents.setDemoModeStatus(!xdrDemoModeStatus).toPromise();

      if (!xdrDemoModeStatus) {
        this.view.dom.$gridSmartViews.controller.setDefaultViewAsActiveView();
      }

      const activeView = this.view.dom.$gridSmartViews.controller.getActiveView();
      await this.view.buildGrid(this.view.allColumns, convertGenericViewConfigToGridViewConfig(activeView));
      toggleActive(this.view.dom.$demoModeButton, !xdrDemoModeStatus);
      this.view.dom.$demoModeButton.tooltipText = !xdrDemoModeStatus
        ? translateFn('Hide demo incident')
        : translateFn('Show demo incident');

      Application.Main.getUser()['xdrDemoModeEnabled'] = !xdrDemoModeStatus;
    } catch (error) {}
  }

  handleViewIncidentEvents(event) {
    if (event.type === DropDownSelectEvents.viewEvents) {
      Application.lib.Navigation.openInNewTab(`/xdr/events/${event.detail.incident._id}`);
    }
  }

  handleResourceIconClicked(event) {
    const incidentsSideMenuComponent = this.view.buildIncidentsSideMenuComponent(
      event.detail.incidentId,
      event.detail.resources,
      event.detail.type,
      PanelType.RESOURCES_PANEL
    );
    this.view.dom.$sideMenu.setMenuContent(incidentsSideMenuComponent);
    this.view.dom.$sideMenu.openSideMenu();
  }

  handleEdrShowGraphClicked(event) {
    this.dependencies.Ext.showIncidentView(event.detail.incident);
    this.closeIncidentDetailsSideMenu();
  }

  handleEdrShowEventsClicked(event) {
    this.dependencies.Ext.showIncidentView(event.detail.incident, false, { showEvents: true });
    this.closeIncidentDetailsSideMenu();
  }

  handleShowIncident(event) {
    this.dependencies.Ext.showIncidentView(event.detail.incident, false, event.detail.extraDetails);
    this.closeIncidentDetailsSideMenu();
  }

  closeIncidentDetailsSideMenu(fireEvent = false) {
    this.view.dom.$sideMenu.closeSideMenu(fireEvent);
    this.view.dom.$sideMenu.setMenuContent(null);
  }

  handleEntityIconClicked(event) {
    const incidentsSideMenuComponent = this.view.buildIncidentsSideMenuComponent(
      event.detail.incidentId,
      event.detail.entities,
      event.detail.type,
      PanelType.ENTITIES_PANEL
    );
    this.view.dom.$sideMenu.setMenuContent(incidentsSideMenuComponent);
    this.view.dom.$sideMenu.openSideMenu();
  }

  handleRowClicked(event) {
    const item = event.detail.rowDetails;

    if (item && item.type === INCIDENT_TYPE.XDR) {
      this.view.setXdrIncidentsSideMenu(item);
      return;
    }

    if (item && item.type === INCIDENT_TYPE.EDR) {
      this.view.setEdrIncidentSideMenu(item);
    }
  }

  handleRightSideNavClosed() {
    this.view.removeActiveStateFromGzIcon();
    // sidemenu can be only hidden at this point, because the setMenuContent will not make it visible next time
    elementClassListRemove(this.view.dom.$sideMenu.shadowRoot?.querySelector('#sideMenu'), ElementClass.HIDDEN);
    this.view.dom.$sideMenu.setMenuContent(null);

    const alertPanel = Ext.ComponentQuery.query('edr-eventDetailsPanel')[0];
    if (alertPanel) {
      alertPanel.up()?.remove(alertPanel);
    }
  }

  /**
   * in case the deleted view is actually the current active view in the grid
   * @param {WebComponentEvent} event
   */
  handleSmartViewAfterDelete(event: WebComponentEvent) {
    const view = event.detail.deletedView;
    if (view._id === this.view.dom.$grid.viewId) {
      const activeView = this.view.dom.$gridSmartViews.getActiveView();
      this.view.buildGrid(this.view.allColumns, convertGenericViewConfigToGridViewConfig(activeView));
    }
  }

  handleNavResizeEvent(event: WebComponentEvent) {
    if (event.target.id === 'sideMenu') {
      return;
    }

    this.view.smartViewSettings.panelSize = event.detail.navBarWidth;
    this.view.dom.$gridSmartViews.updateSmartViewsSettings(this.view.smartViewSettings);
  }

  /**
   * after a view is created the $gridSmartViews component will dispatch the SmartViewEvents.SMART_VIEW_AFTER_CREATE event
   * we need to rebuild the grid with the newly created smart view
   */
  handleSmartViewAfterCreate(event: WebComponentEvent) {
    this.view.buildGrid(this.view.allColumns, convertGenericViewConfigToGridViewConfig(event.detail.createdView));
  }

  /**
   * the updated view name is for the current opened view
   * we need to also update the view name in the grid.
   */
  handleSmartViewNameUpdated(event: WebComponentEvent) {
    GenericGridSmartViewsHelpers.handleSmartViewNameUpdated(event, {
      gridElement: this.view.dom.$grid,
      smartViewsElement: this.view.dom.$gridSmartViews,
    });
  }

  handleSidenavOpened(event: WebComponentEvent) {
    this.view.smartViewSettings.isCollapsed = false;
    this.view.dom.$gridSmartViews.updateSmartViewsSettings(this.view.smartViewSettings);
  }

  handleSidenavClosed(event: WebComponentEvent) {
    this.view.smartViewSettings.isCollapsed = true;
    this.view.dom.$gridSmartViews.updateSmartViewsSettings(this.view.smartViewSettings);
  }

  handleSmartViewFavoriteChanged(event: WebComponentEvent) {
    GenericGridSmartViewsHelpers.handleSmartViewFavoriteChanged({
      gridElement: this.view.dom.$grid,
      smartViewsElement: this.view.dom.$gridSmartViews,
    });
  }

  handleSmartViewClicked(event: WebComponentEvent) {
    const smartViewConfiguration = event.detail.smartViewConfiguration;
    if (this.view.dom.$grid.viewId === event.detail.smartViewConfiguration._id) {
      return;
    }
    this.view.dom.$gridSmartViews.controller.updateActiveViewId(event.detail.smartViewConfiguration._id);
    const convertedConfig = convertGenericViewConfigToGridViewConfig(smartViewConfiguration);
    this.view.buildGrid(this.view.allColumns, convertedConfig);
  }

  handleSelectionChange(event) {
    const demoIncident = event.detail.selectionStoreData.filter(incident => incident.isGzDemo === true)[0];
    this.selectedItems = demoIncident
      ? event.detail.selectionStoreData.filter(incident => demoIncident._id !== incident._id)
      : event.detail.selectionStoreData;
    this.view.dom.changeStatusBulk.$button.disabled = this.selectedItems.length === 0;
    this.view.dom.prioritizeBulk.$button.disabled = this.selectedItems.length === 0;
    this.view.dom.$assignBulkButton.disabled = this.selectedItems.length === 0;
  }

  getAdditionalElementComponent(additionalElement) {
    if (additionalElement.hasSearch) {
      return `<div>
        <gz-form-field label='${additionalElement.label}'>
            <gz-single-select
            class="d-block pb-2"
            gz-form-input
            cannotDelete
            value="${additionalElement.value}"
            filterOptions='${JSON.stringify(additionalElement.options)}'
            searchPlaceholderString='${translateFn('Search')}'
            noSearchResultsString='${translateFn('No results found')}'
            ></gz-single-select>
        </gz-form-field>
      </div>`;
    }

    return `<div>
      <gz-form-field label='${additionalElement.label}'>
          <gz-single-select
          class="d-block pb-2"
          gz-form-input
          cannotDelete
          value="${additionalElement.value}"
          filterOptions='${JSON.stringify(additionalElement.options)}'
          ></gz-single-select>
      </gz-form-field>
    </div>`;
  }

  renderDialogHTML(hasCorrelatedCheckbox, textChangeBulk, textCorrelatedCheckbox, additionalElement) {
    let result = '';

    result += `
            <div>
                <gz-text class="pb-2" fontSize="14">${textChangeBulk}
                </gz-text>
            </div>
        `;

    if (additionalElement) {
      result += this.getAdditionalElementComponent(additionalElement);
    }

    if (hasCorrelatedCheckbox) {
      result += `
                <div>
                  <gz-form-field>
                    <gz-checkbox class="pb-2" gz-form-input label='${textCorrelatedCheckbox}'></gz-checkbox>
                  </gz-form-field>
                </div>
            `;
    }

    result += `
            <div>
                <gz-form-field class='label-mw-250' label=''>
                    <gz-textarea gz-form-input hasCounter='true' maxChars='2048' placeholder='${translateFn(
                      'Notes'
                    )}'></gz-textarea>
                    <gz-input-error></gz-input-error>
                 </gz-form-field>
            </div>
        `;

    return result;
  }

  async createActionDialog(incidents, fieldName, fieldValue, texts: DialogActionTexts, additionalElement: AdditionalElementInfo|null = null) {
    const xdrIncidents = incidents.filter(incident => incident.type === INCIDENT_TYPE.XDR);
    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = this.renderDialogHTML(xdrIncidents.length !== 0, texts.changeBulk, texts.correlatedCheckbox, additionalElement);

    const dialogFooter = document.createElement('div');
    const confirmButton = new GzButtonWebComponent();
    const cancelButton = new GzButtonWebComponent();
    confirmButton.innerText = translateFn('Change');
    cancelButton.innerText = translateFn('Cancel');
    cancelButton.setAttribute('type', 'outline');
    cancelButton.setAttribute('color', 'secondary');
    dialogFooter.appendChild(confirmButton);
    dialogFooter.appendChild(cancelButton);

    const dialogConfigs: GzDialog = {
      headline: texts.headline,
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
    };
    const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs);

    dialog.updateComplete.then(() => {
      const singleSelect = additionalElement
        ? dialog.shadowRoot?.querySelector('gz-single-select') as GzSingleSelectWebComponent
        : null;

      if (additionalElement && singleSelect && singleSelect.getFormValue() === null) {
        singleSelect.setFormValue(additionalElement.value);
      }
    });


    confirmButton.addEventListener('click', async () => {
      if (confirmButton.showSpinner) {
        return;
      }

      try {
        confirmButton.showSpinner = true;
        const note = (dialog.shadowRoot?.querySelector('gz-textarea') as GzTextareaWebComponent).getFormValue();
        const singleSelectValue = additionalElement
          ? (dialog.shadowRoot?.querySelector('gz-single-select') as GzSingleSelectWebComponent).getFormValue()
          : null;
        const changeCorrelatedCheckbox = dialog.shadowRoot?.querySelector('gz-checkbox') as GzCheckboxWebComponent;

        const response = await ConsoleRequest.createRequest<any>({
          dataProviderType: DataProviderType.ENDPOINTS,
          endpoint: 'UnifiedIncidents',
          method: 'changeBulkIncidents',
          payload: {
            incidentIds: incidents.map(incident => incident._id),
            fields: {[fieldName]: additionalElement ? singleSelectValue : fieldValue},
            changeCorrelated: changeCorrelatedCheckbox ? changeCorrelatedCheckbox.getFormValue() : false,
            note,
          },
        });
        this.showBulkActionToast(texts.successToast, ToastSeverity.SUCCESS);
      } catch (error) {
        this.showBulkActionToast(texts.errorToast, ToastSeverity.ERROR);
      }

      dialog.close();
      await this.view.dom.$grid.controller.handleGridItemsLoad(true, true);
    });

    cancelButton.addEventListener('click', () => {
      dialog.close();
    });
  }

  async handleChangeStatusBulkSelectionChanged(event: WebComponentEvent) {
    const newStatus = event.detail.selected;
    await this.createActionDialog(this.selectedItems, BulkActionFields.STATUS, newStatus,
      {
        headline: translateFn('Change status?'),
        changeBulk: translateFn(
          'The status of the selected incidents will change to <b>{STATUS}</b>.',
          { STATUS: Translator.translateEdrIncidentStatus(newStatus) }
        ),
        correlatedCheckbox: translateFn('Apply status change to all correlated incidents'),
        successToast: translateFn('Status changed successfully.'),
        errorToast: translateFn('Failed to change the status.'),
      });
  }

  async handlePrioritizeBulkSelectionChanged(event: WebComponentEvent) {
    const newPriority = event.detail.selected;
    await this.createActionDialog(this.selectedItems, BulkActionFields.PRIORITY, newPriority,
      {
        headline: translateFn('Change priority?'),
        changeBulk: translateFn(
          'The priority of the selected incidents will change to <b>{PRIORITY}</b>.',
          { PRIORITY: Translator.translateEdrIncidentPriority(newPriority) }
        ),
        correlatedCheckbox: translateFn('Apply the priority change to all correlated incidents'),
        successToast: translateFn('Priority changed successfully.'),
        errorToast: translateFn('Failed to change the priority.'),
      });
  }

  async handleAssignBulkChange(event: WebComponentEvent) {
    const newAssignee = event.detail.id;
    const newAAssigneeName = event.detail.id === ''
      ?  translateFn('Unassigned')
      : this.view.availableAssignees.find(assignee => assignee._id === newAssignee)?.profile?.fullName;

    await this.createActionDialog(this.selectedItems, BulkActionFields.ASSIGNED_USER, newAssignee,
      {
        headline: translateFn('Change assignee?'),
        changeBulk: translateFn(
          'The assignee of the selected incidents will change to <b>{ASSIGNEE}</b>.',
          { ASSIGNEE: newAAssigneeName }
        ),
        correlatedCheckbox: translateFn('Apply the assignee change to all correlated incidents'),
        successToast: translateFn('Assignee changed successfully.'),
        errorToast: translateFn('Failed to change the assignee.'),
      });
  }

  async handlePrioritizeInlineAction(event: WebComponentEvent) {
    const filterOptions = [
      {
        label: translateFn('Unassigned'),
        value: PRIORITY.UNASSIGNED,
      },
      {
        label: translateFn('Critical'),
        value: PRIORITY.CRITICAL,
      },
      {
        label: translateFn('High'),
        value: PRIORITY.HIGH,
      },
      {
        label: translateFn('Medium'),
        value: PRIORITY.MEDIUM,
      },
      {
        label: translateFn('Low'),
        value: PRIORITY.LOW,
      },
    ];

    await this.createActionDialog([event.detail.incident], BulkActionFields.PRIORITY, null,
      {
        headline: translateFn('Change priority?'),
        changeBulk: translateFn('Select the priority of this incident.'),
        correlatedCheckbox: translateFn('Apply the priority change to all correlated incidents'),
        successToast: translateFn('Priority changed successfully.'),
        errorToast: translateFn('Failed to change the priority.'),
      },
      {
        label: translateFn('Priority'),
        value: event.detail.incident.priority,
        options: filterOptions,
      });
  }

  async handleAssignInlineAction(event: WebComponentEvent) {
    await this.createActionDialog([event.detail.incident], BulkActionFields.ASSIGNED_USER, null,
      {
        headline: translateFn('Change assignee?'),
        changeBulk: translateFn('Select the assignee of this incident.'),
        correlatedCheckbox: translateFn('Apply the assignee change to all correlated incidents'),
        successToast: translateFn('Assignee changed successfully.'),
        errorToast: translateFn('Failed to change the assignee.'),
      },
      {
        label: translateFn('Assignee'),
        value: event.detail.incident.assignedUser.id ?? '',
        options: this.view.assigneesOptions,
        hasSearch: true,
      });
  }

  private showBulkActionToast(message, severity) {
    const toastConfig: GzToast = {
      message: message,
      severity: severity,
      x: ToastPositionX.RIGHT,
      y: ToastPositionY.BOTTOM,
      autoCloseDelayMs: 5000,
    };

    GzToastNotificationWebComponent.buildToastWrapperElement(toastConfig);
  }

  /**
   * defaultViewProps -> when clicking the reset grid filters this is the view that the grid will reset to
   * activeViewProps -> current active grid configuration( tracks changes)
   * activeInitialViewProps -> initial active grid configuration
   * @param {WebComponentEvent} event
   */
  updateViewProps(event: WebComponentEvent) {
    this.view.dom.$gridSmartViews.updateSmartView(this.processSmartViewForUpdate(event.detail.viewConfiguration));
    this.view.dom.$grid.activeViewProps = event.detail.viewConfiguration;
    this.view.dom.$grid.activeViewInitialProps = event.detail.viewConfiguration;
    this.view.dom.$grid.defaultViewProps = event.detail.viewConfiguration;
  }

  /**
   * will open a new dialog where the user can create a new view
   * after the view is created $gridSmartViews will dispatch the following event :
   * SmartViewEvents.SMART_VIEW_AFTER_CREATE with the newly created view
   */
  async showSaveAsDialog() {
    const viewConfiguration = {
      filters: this.view.dom.$grid.activeViewProps.filters,
      displayedColumns: this.view.dom.$grid.activeViewProps.displayedColumns,
      compactView: this.view.dom.$grid.activeViewProps.compactView,
      columnConfigs: this.view.dom.$grid.activeViewProps.columnConfigs,
      pageSize: this.view.dom.$grid.activeViewProps.pageSize,
    };
    const { smartViewsStrings } = this.view.getTranslations();
    const viewCreationConfig = {
      dialogText: smartViewsStrings.saveGridViewFormDialog.dialogText,
    };
    await this.view.dom.$gridSmartViews.saveSmartView(
      this.convertGridConfigToGenericConfig(viewConfiguration),
      viewCreationConfig
    );
  }

  processSmartViewForUpdate(viewConfig): SmartViewsUpdateRequest {
    return {
      id: viewConfig.id,
      details: {
        specifics: {
          filters: viewConfig.filters,
          columnConfigs: viewConfig.columnConfigs,
          displayedColumns: viewConfig.displayedColumns,
          pageSize: viewConfig.pageSize,
          compactView: viewConfig.compactView,
        },
      },
    };
  }

  convertGridConfigToGenericConfig(viewConfig): SmartViewsCreateRequest {
    return {
      category: Categories.SAVED,
      type: SmartViewType.GRID_VIEW,
      specifics: {
        filters: viewConfig.filters,
        columnConfigs: viewConfig.columnConfigs,
        displayedColumns: viewConfig.displayedColumns,
        pageSize: viewConfig.pageSize,
        compactView: viewConfig.compactView,
      },
    };
  }

  private handleAlertOpened() {
    // Hide only sidemenu, not overlay
    elementClassListAdd(this.view.dom.$sideMenu.shadowRoot?.querySelector('#sideMenu'), ElementClass.HIDDEN);
    this.view.setExtAlertPanelWidth();
  }

  private handleAlertClosed() {
    elementClassListRemove(this.view.dom.$sideMenu.shadowRoot?.querySelector('#sideMenu'), ElementClass.HIDDEN);
  }
}
